<div class="table-row-input-container">
  <!-- Always show table structure (with headers) if columns are defined -->
  @if (columns && columns.length > 0) {
    <div class="table-wrapper">
      <table class="table-rows">
        <!-- Header row -->
        <thead>
          <tr class="table-header-row">
            <!-- Add Row button in header (first column position) -->
            <th class="table-action-column">
              @if (!isReadOnly && !isExtracting) {
                <button 
                  class="btn-add-row-header" 
                  (click)="addNewRow()" 
                  title="Add new row"
                  [disabled]="isReadOnly || isExtracting">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                </button>
              }
            </th>
            <!-- Column headers -->
            @for (column of columns; track trackByColumnName(column)) {
              <th class="table-header-cell" [title]="column.name">{{ column.name }}</th>
            }
          </tr>
        </thead>

        <!-- Body rows or empty state -->
        <tbody>
          @if (rows && rows.length > 0) {
            @for (row of rows; track trackByRowIndex($index); let rowIndex = $index) {
              <tr class="table-data-row" [class.editing-row]="editingRowIndex === rowIndex"
                  [class.auto-populated-row]="isAutoPopulated"
                  [class.overridden-row]="isOverridden">
                <!-- Row index indicator + remove button -->
                <td class="table-row-number">
                  <div class="row-number-content">
                    @if (isAutoPopulated) {
                      <span class="auto-populated-dot"
                            [class.overridden-dot]="isOverridden"
                            [class.bbox-indicated]="rowHasAnyBbox(rowIndex)"
                            [title]="rowHasAnyBbox(rowIndex) ? 'Auto-populated, Detected Region' : 'Auto-populated'">
                      </span>
                    }
                    <span class="row-number">{{ rowIndex + 1 }}</span>
                    @if (!isReadOnly && !isExtracting) {
                      <button
                        class="btn-remove-row"
                        (click)="removeRow(rowIndex); $event.stopPropagation()"
                        title="Remove row">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                      </button>
                    }
                  </div>
                </td>

                <!-- Data cells -->
                @for (column of columns; track trackByColumnName(column); let colIndex = $index) {
                  <td
                    class="table-cell"
                    [class.editing]="isCellEditing(rowIndex, column.name)"
                    [class.activating]="activatingRowColumn?.rowIndex === rowIndex && activatingRowColumn?.columnName === column.name"
                    [class.has-value]="getCellValue(rowIndex, column.name)"
                    [class.auto-populated]="isAutoPopulated"
                    [class.overridden-cell]="isOverridden"
                    (mousedown)="setActivatingState(rowIndex, column.name)"
                    (click)="onCellClick(rowIndex, column.name, $event); clearActivatingState()"
                    [title]="isCellEditing(rowIndex, column.name) ? '' : getCellTooltip(rowIndex, column.name)">
                    
                    <!-- Display mode -->
                    @if (!isCellEditing(rowIndex, column.name)) {
                      @if (isDateColumn(column.name)) {
                        <div class="date-display-wrapper">
                          <span class="cell-value" [class.empty]="!getCellValue(rowIndex, column.name)">
                            {{ getCellValue(rowIndex, column.name) || 'MM/DD/YYYY' }}
                          </span>
                          <span class="material-icons date-picker-icon">calendar_today</span>
                        </div>
                      } @else {
                        <span class="cell-value" [class.empty]="!getCellValue(rowIndex, column.name)">
                          {{ getCellValue(rowIndex, column.name) || '—' }}
                        </span>
                      }
                    }

                    <!-- Edit mode -->
                    @if (isCellEditing(rowIndex, column.name)) {
                      @if (isDateColumn(column.name)) {
                        <div class="date-input-wrapper">
                          <input
                            #activeCellInput
                            type="text"
                            class="cell-input date-input"
                            [value]="editingValue"
                            [title]="editingValue"
                            (input)="onDateCellInput($event)"
                            (blur)="onCellBlur(rowIndex, column.name)"
                            (keydown)="onCellKeydown($event, rowIndex, column.name)"
                            placeholder="MM/DD/YYYY"
                            maxlength="10">
                          <input
                            type="date"
                            class="date-picker-hidden"
                            [value]="getDatePickerValue()"
                            (change)="onDatePickerChange($event)">
                          <span class="material-icons date-picker-icon" (mousedown)="onDatePickerIconMousedown($event)" (click)="openDatePicker($event)">calendar_today</span>
                        </div>
                      } @else {
                        <input
                          #activeCellInput
                          type="text"
                          class="cell-input"
                          [(ngModel)]="editingValue"
                          [title]="editingValue"
                          (blur)="onCellBlur(rowIndex, column.name)"
                          (keydown)="onCellKeydown($event, rowIndex, column.name)">
                      }
                    }

                    @if (isCellModified(rowIndex, column.name)) {
                      <button type="button"
                              class="cell-undo-btn"
                              (mousedown)="$event.preventDefault(); $event.stopPropagation()"
                              (click)="undoCellEdit(rowIndex, column.name, $event)"
                              title="Undo cell edit">
                        <span class="material-icons" aria-hidden="true">undo</span>
                      </button>
                    }

                    @if (canSnip) {
                      <button type="button"
                              class="cell-snip-toggle"
                              [class.has-pending]="hasPendingSnip(rowIndex, column.name)"
                              (mousedown)="$event.preventDefault(); $event.stopPropagation()"
                              (click)="snipToggleClicked.emit({ rowIndex: rowIndex, columnName: column.name, event: $event }); $event.stopPropagation()"
                              [title]="hasPendingSnip(rowIndex, column.name) ? 'Undo bounding box change' : 'Draw bounding box for this cell'">
                        <span class="material-icons" aria-hidden="true">{{ hasPendingSnip(rowIndex, column.name) ? 'undo' : 'crop_free' }}</span>
                      </button>
                    }
                  </td>
                }
              </tr>
            }
          } @else {
            <!-- Empty state row -->
            <tr class="table-empty-row">
              <td [attr.colspan]="columns.length + 1" class="empty-cell">
                <p class="empty-message">No rows added yet</p>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  } @else {
    <!-- No columns defined -->
    <div class="empty-state-small">
      <p>Table configuration error: No columns defined</p>
    </div>
  }
</div>
